import { useMemo } from 'react';
import { format, isValid } from 'date-fns';
import {
  useTheme, 
  useMediaQuery,
  Box, 
  Typography,
  Card, 
  CardHeader, 
  CardContent,
  Chip, 
  Tooltip,
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TableContainer,
  Pagination,
} from '@mui/material';
import { ArrowForward, CheckCircle, WarningAmber } from '@mui/icons-material';


const renderCell = (val) => {
  if (val === null || val === undefined) return <em style={{ opacity: 0.4 }}>null</em>;
  if (typeof val === 'boolean') return String(val);
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
    const d = new Date(val);
    return isValid(d) ? format(d, 'dd/MM/yyyy HH:mm') : val;
  }
  if (typeof val === 'object') {
    const s = JSON.stringify(val);
    return s.length > 60 ? s.slice(0, 57) + '…' : s;
  }
  return String(val);
};

const PreviewCard = ({ previewData, previewPage, onPageChange }) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const columns = useMemo(() => {
    if (!previewData?.data?.length) return [];
    return Object.keys(previewData.data[0]).filter(k => k !== '__v');
  }, [previewData]);

  if (!previewData) return null;

  return (
    <Card 
      sx={{
        flexShrink: 0,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        minWidth: 0,
      }}
    >
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={600}>Preview</Typography>
            <Chip
              icon={previewData.total > 0
                ? <WarningAmber sx={{ fontSize: 14 }} />
                : <CheckCircle sx={{ fontSize: 14 }} />}
              label={`${previewData.total.toLocaleString()} record(s) will be deleted`}
              color={previewData.total > 0 ? 'error' : 'success'}
              size="small"
              variant="outlined"
            />
          </Box>
        }
        sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      />

      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {previewData.data.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">No records match the filters.</Typography>
          </Box>
        ) : (
          <>
            {isMobile && (
              <Box px={2} py={0.75} display="flex" alignItems="center" gap={0.5}>
                <ArrowForward sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">Scroll to see all columns</Typography>
              </Box>
            )}

            <TableContainer sx={{ maxHeight: isMobile ? 260 : 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 40, width: 40 }}>#</TableCell>
                    {columns.map(col => (
                      <TableCell key={col} sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                        <Typography variant="caption" fontWeight={600}>{col}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.data.map((row, i) => (
                    <TableRow key={String(row._id ?? i)} hover>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {(previewData.page - 1) * previewData.pageSize + i + 1}
                        </Typography>
                      </TableCell>
                      {columns.map(col => (
                        <TableCell
                          key={col}
                          sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          <Tooltip title={String(row[col] ?? '')} placement="top">
                            <Typography variant="caption">{renderCell(row[col])}</Typography>
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {previewData.totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                py={1.5}
                borderTop="1px solid"
                borderColor="divider"
              >
                <Pagination
                  count={previewData.totalPages}
                  page={previewPage}
                  onChange={(_, p) => onPageChange(p)}
                  size="small"
                  color="primary"
                  siblingCount={isMobile ? 0 : 1}
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PreviewCard;
